/**
 * Continuous MFN enforcement orchestrator — the 2026-07-01 master-plan
 * decision ("predictive vs reactive"): the deterministic MFN engine
 * re-evaluates a fund automatically whenever the underlying facts change (a
 * provision clears human review, a commitment moves, an MFN clause is
 * edited), not only when a user remembers to press "run".
 *
 * `enforceMfnForFund` is the single entry point every seam calls:
 *   - review-queue.tsx / document-review.tsx  → source: 'provision_reviewed'
 *   - api/commitments.ts createCommitment      → source: 'commitment_changed'
 *   - pipeline/run-extraction.ts               → source: 'clause_changed'
 *   - mfn-list.tsx / document-review.tsx (manual button) → source: 'manual'
 *
 * It reuses `gatherEvaluateInsertMfn` (run-mfn.ts) for the actual gather/
 * evaluate/insert cycle — there is exactly one implementation of that. On top
 * of it, it appends one row to the append-only `mfn_evaluations` audit log and
 * flags/revives stale triggers via `diffTriggers` (mfn-diff.ts).
 *
 * FAIL-SOFT (critical, but narrowly scoped — 2026-07-03 audit fix): the demo
 * tenant runs on live prod Supabase and migration 20260702200000
 * (mfn_evaluations + mfn_triggers.stale/stale_at/last_evaluation_id) lands
 * separately from this code. The audit-log-and-stale-diff step is wrapped in
 * its own try/catch: if that table/those columns don't exist yet, it warns
 * once and the function still returns the core run summary (clause/
 * candidate/trigger counts) exactly as `runMfnEvaluation` always has —
 * `evaluationId: null` is the signal that the new half didn't run. Callers
 * that want to know "did anything actually change" (for the auto-triggered
 * seams' toast) must check `evaluationId !== null` — see `describeMfnDelta`
 * below.
 *
 * The CORE gather/evaluate/insert step (`gatherEvaluateInsertMfn`) is a
 * different story: it only touches tables that predate migration
 * 20260702200000 (mfn_clauses, provisions, fund_lp_commitments,
 * mfn_triggers), so there is no legitimate "not-migrated-yet" error it can
 * throw. A 2026-07-03 audit found the original code swallowed EVERY error
 * from this step — including a genuine permission-denied (42501), network,
 * or constraint failure — and returned `null` with no other signal. For a
 * compliance product that is the worst failure mode: an MFN trigger silently
 * fails to compute and nothing tells anyone. `enforceMfnForFund` now lets a
 * genuine core-step failure propagate (re-throw) after logging it, so it
 * reaches each call site's own error handling (`onError` on the manual-run
 * mutations; the fire-and-forget seams below now log to the console instead
 * of swallowing to `undefined`). Only the audit-log/stale-diff block below
 * still fail-softs to `null`/`base`, and only for the specific pre-migration
 * shape described above.
 */

import { MFN_ENGINE_VERSION } from "@argus/engine";
import { gatherEvaluateInsertMfn } from "@/lib/pipeline/run-mfn";
import { diffTriggers } from "@/lib/pipeline/mfn-diff";
import {
  insertMfnEvaluation,
  listMfnTriggersForDiff,
  markMfnTriggersRevived,
  markMfnTriggersStale,
  type MfnEvaluationTriggerSource,
} from "@/lib/api/mfn";

export interface EnforceMfnArgs {
  tenantId: string;
  fundId: string;
  source: MfnEvaluationTriggerSource;
  /** The provision/commitment/clause id that caused the run, for the audit trail. */
  sourceRef?: string | undefined;
  userId?: string | undefined;
}

export interface EnforcementSummary {
  clauseCount: number;
  candidateCount: number;
  computedTriggers: number;
  newTriggers: number;
  /** 0 whenever the fail-soft guard tripped (see `evaluationId`). */
  newlyStale: number;
  /** 0 whenever the fail-soft guard tripped (see `evaluationId`). */
  revived: number;
  /** The mfn_evaluations row id, or null when the audit-log/stale-diff step
   * couldn't run (migration not applied yet). The core counts above are
   * still real and complete either way. */
  evaluationId: string | null;
  triggerSource: MfnEvaluationTriggerSource;
}

/**
 * Runs the engine for a fund and appends one row to the continuous-
 * enforcement audit log.
 *
 * 2026-07-03 audit fix: this function used to swallow ANY error from the
 * core gather/evaluate/insert step and resolve `null`, on the theory that no
 * call site should be blocked by enforcement. That conflated two very
 * different failure shapes: a pre-migration "table doesn't exist yet" (truly
 * benign, still handled below) and a real failure — permission denied,
 * network, a constraint violation — which must never be silently dropped for
 * a compliance product. The core step now propagates a genuine failure
 * (logged here, then re-thrown) so it reaches the caller: `useMutation`
 * callers see it via `onError`; fire-and-forget callers (provision review,
 * commitment writes, extraction) now log it instead of swallowing to
 * `undefined`. Only the audit-log/stale-diff half below (the part that
 * actually depends on the not-yet-guaranteed-applied migration) still
 * fail-softs.
 */
export async function enforceMfnForFund(args: EnforceMfnArgs): Promise<EnforcementSummary | null> {
  const { tenantId, fundId, source, sourceRef, userId } = args;

  let core;
  try {
    core = await gatherEvaluateInsertMfn(tenantId, fundId);
  } catch (err) {
    console.error(
      `[continuous-mfn] enforceMfnForFund(fund=${fundId}, source=${source}) — core gather/` +
        `evaluate/insert failed. This is a REAL failure (not a pre-migration gap) and is NOT ` +
        `swallowed — re-throwing so the caller surfaces it:`,
      err,
    );
    throw err;
  }

  const base: EnforcementSummary = {
    clauseCount: core.clauseCount,
    candidateCount: core.candidateCount,
    computedTriggers: core.computedTriggers,
    newTriggers: core.newTriggers,
    newlyStale: 0,
    revived: 0,
    evaluationId: null,
    triggerSource: source,
  };

  // ---- fail-soft guard: mfn_evaluations insert + stale/revived updates ----
  // Everything in this block touches migration-20260702200000 schema. Any
  // failure here (42P01 relation-does-not-exist pre-migration, or an
  // unexpected column error) is caught, warned once, and swallowed — the core
  // summary above is still returned so existing UI (e.g. the manual "Run MFN
  // evaluation" button) keeps behaving byte-identically pre-migration.
  try {
    const existing = await listMfnTriggersForDiff(tenantId, fundId);
    const computed = core.triggers.map((t) => ({
      mfnClauseId: t.mfnClauseId,
      triggeringProvisionId: t.triggeringProvisionId,
    }));
    const { newlyStaleIds, revivedIds } = diffTriggers(computed, existing);

    const evaluationId = await insertMfnEvaluation({
      tenant_id: tenantId,
      fund_id: fundId,
      engine_version: MFN_ENGINE_VERSION,
      trigger_source: source,
      source_ref: sourceRef ?? null,
      clause_count: core.clauseCount,
      candidate_count: core.candidateCount,
      computed_triggers: core.computedTriggers,
      new_triggers: core.newTriggers,
      newly_stale: newlyStaleIds.length,
      ran_by: userId ?? null,
    });

    await Promise.all([
      markMfnTriggersStale(newlyStaleIds, evaluationId),
      markMfnTriggersRevived(revivedIds, evaluationId),
    ]);

    return { ...base, newlyStale: newlyStaleIds.length, revived: revivedIds.length, evaluationId };
  } catch (err) {
    warnEvaluationsWriteFailed(err);
    return base;
  }
}

let warnedEvaluationsWriteFailed = false;
function warnEvaluationsWriteFailed(err: unknown): void {
  if (warnedEvaluationsWriteFailed) return;
  warnedEvaluationsWriteFailed = true;
  console.warn(
    "[continuous-mfn] mfn_evaluations audit log / stale-trigger update unavailable " +
      "(migration 20260702200000 not applied yet) — evaluation still ran, just not logged:",
    err,
  );
}

/**
 * Human-readable delta for the sonner toast the auto-triggered seams (review,
 * commitment, clause writes) show, or null when there's nothing worth
 * surfacing — no deltas, or the fail-soft guard tripped (silent, per spec: a
 * pre-migration auto-run should not visibly announce itself since it can't
 * confirm what actually changed in the trigger set).
 */
export function describeMfnDelta(summary: EnforcementSummary | null): string | null {
  if (!summary?.evaluationId) return null;
  const parts: string[] = [];
  if (summary.newTriggers > 0) {
    parts.push(`${summary.newTriggers} new trigger${summary.newTriggers === 1 ? "" : "s"}`);
  }
  if (summary.newlyStale > 0) {
    parts.push(`${summary.newlyStale} flagged stale`);
  }
  if (summary.revived > 0) {
    parts.push(`${summary.revived} revived`);
  }
  return parts.length > 0 ? parts.join(" · ") : null;
}
